<script lang="ts">
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import cofeeBeanIcon from "@core/assets/icons/cofee-bean.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import type { Country } from "@core/schema/Country.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getOrderSummaryRows } from "@core/schema/utils/getOrderSummaryRows.js";
	import { shouldApplyTariffs } from "@core/schema/utils/shouldApplyTariffs.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { CartStep, cartStepArray } from "../../cart/CartStep.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CartOrderSummary.translations.json" with { type: "json" };
	import CartSummaryCourse from "./CartSummaryCourse.svelte";
	import CartSummaryProduct from "./CartSummaryProduct.svelte";
	import CartSummarySubscription from "./CartSummarySubscription.svelte";
	import CartSummaryTable from "./CartSummaryTable.svelte";
	import CouponInput from "./CouponInput.svelte";
	import CurrencySwitcher from "./CurrencySwitcher.svelte";

	const { cartCountry, currentStep }: { cartCountry?: Country; currentStep: CartStep } = $props();
	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();
	const cart = $derived(Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery));

	const orderProducts = $derived(Cart.orderProducts);
	const showTarriffWarning = $derived(cartCountry !== undefined && shouldApplyTariffs(cartCountry));
</script>

<div class="flex flex-col lg:sticky lg:top-32">
	{#if !Cart.isEmpty}
		<div class="flex justify-between items-center mb-5">
			<Heading level={2} text={translate("summaryTitle")} class="font-antipol font-medium text-6/none" />
			{#if cartStepArray.indexOf(currentStep) > 0}
				<CurrencySwitcher />
			{/if}
		</div>
	{/if}
	<div class="mt-5 mb-10 flex flex-col gap-2 lg:max-h-118 lg:overflow-y-auto overflow-x-hidden">
		{#if cart.hasSubscription}
			<CartSummarySubscription subscription={cart.subscription} />
		{:else if cart.hasCourseOrder && cart.courseOrder.term}
			<CartSummaryCourse term={cart.courseOrder.term} />
		{:else}
			{#each orderProducts as orderProduct (orderProduct.id)}
				<CartSummaryProduct {orderProduct} />
			{:else}
				<div class="text-center mt-10 items-center flex flex-col gap-4">
					<Icon icon={cofeeBeanIcon} class="size-12 text-fcrbrown" />
					<span class="font-antipol text-fcrdarkbrown font-medium text-6">
						<Formatted text={translate("cartEmpty")} />
					</span>
					<Link
						href={getCategoryPath(tenant.locale, CategoryType.Coffee)}
						size={ButtonSize.Medium}
						variant={ButtonVariant.Dark}
						class="mt-4"
					>
						<Formatted text={translate("currentOffer")} />
					</Link>
				</div>
			{/each}
		{/if}
	</div>
	{#if Cart.itemCount > 0 && Cart.currency}
		<CartSummaryTable
			tarriffWarning={showTarriffWarning}
			rows={getOrderSummaryRows(
				tenant.locale,
				Cart.currency,
				{ ...cart.order, prices: Cart.price },
				!cart.hasCourseOrder,
			)}
		/>
		<CouponInput class="mt-6" />
	{/if}
</div>
